import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import ListLoadError from '@/components/ListLoadError';
import AttachmentGrid from '@/components/AttachmentGrid';
import { 
  Plus, Save, Search, Filter, Download, Edit2, Trash2, Eye,
  FileText, Package, Truck, Calendar, CheckCircle, AlertCircle,
  X, ChevronDown, RefreshCw, Camera, Upload, AlertTriangle, Loader2,
  Briefcase
} from 'lucide-react';

// Async Searchable Dropdown for large datasets (like commodities)
function AsyncSearchableDropdown({ 
  label, 
  value, 
  selectedDisplay,
  onSearch,
  onChange, 
  placeholder, 
  displayField, 
  valueField,
  required,
  disabled,
  testId 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Debounced search
  const handleSearch = useCallback(async (term) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await onSearch(term);
        setOptions(results);
      } catch (error) {
        console.error('Search error:', error);
        setOptions([]);
      }
      setLoading(false);
    }, 300);
  }, [onSearch]);
  
  // Load initial results when opened
  useEffect(() => {
    if (isOpen && options.length === 0) {
      handleSearch('');
    }
  }, [isOpen]);
  
  // Search when term changes
  useEffect(() => {
    if (isOpen) {
      handleSearch(searchTerm);
    }
  }, [searchTerm, isOpen, handleSearch]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between transition ${
          disabled 
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-300 hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100'
        }`}
        data-testid={testId}
      >
        <span className={selectedDisplay ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDisplay || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search HSN code or name..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={14} className="text-amber-500 animate-spin" />}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-56 overflow-y-auto">
            {loading && options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                <Loader2 size={16} className="animate-spin mx-auto mb-2" />
                Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {searchTerm ? 'No results found' : 'Type to search commodities'}
              </div>
            ) : (
              options.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(opt[valueField], opt);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition border-b border-gray-50 ${
                    opt[valueField] === value ? 'bg-amber-100 text-amber-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  <div className="font-medium">{opt.hsnCode || opt.commodityCode}</div>
                  <div className="text-xs text-gray-500 truncate">{opt.commodityName}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Searchable Dropdown Component
function SearchableDropdown({ 
  label, 
  value, 
  options, 
  onChange, 
  placeholder, 
  displayField, 
  valueField,
  required,
  disabled,
  testId 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  // Find the selected option's display value
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? selectedOption[displayField] : '';
  
  // Filter options based on search
  const filteredOptions = options.filter(opt => 
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between transition ${
          disabled 
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-300 hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100'
        }`}
        data-testid={testId}
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                No results found
              </div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(opt[valueField], opt);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition ${
                    opt[valueField] === value ? 'bg-amber-100 text-amber-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  {opt[displayField]}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GRN() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [grnList, setGrnList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grnLoadError, setGrnLoadError] = useState(null);
  const [grnPage, setGrnPage] = useState(1);
  const [grnTotal, setGrnTotal] = useState(0);
  const [grnHasMore, setGrnHasMore] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [saveStatus, setSaveStatus] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // For delete confirmation
  const [deleteImpact, setDeleteImpact] = useState(null); // Preview of what gets cascaded on delete
  const [grnTab, setGrnTab] = useState('material'); // 'material' or 'service'
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingGRNEdit, setSavingGRNEdit] = useState(false);
  
  // Check if user has edit and delete permissions for stores GRN
  const canEdit = canUserEdit(user, '/stores/grn');
  const canDelete = canUserDelete(user, '/stores/grn');
  
  // Master data
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null); // Store selected PO with its items
  const [poCommodities, setPoCommodities] = useState([]); // Commodities from selected PO
  const [poMiscCharges, setPoMiscCharges] = useState([]); // Misc charges from selected PO
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const submitKeyRef = useRef(null);
  const [capturedImages, setCapturedImages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [imageViewerGRN, setImageViewerGRN] = useState(null);

  // Form state for new GRN (now supports multiple items)
  const [selectedItemKey, setSelectedItemKey] = useState('');
  const [formData, setFormData] = useState({
    grnNumber: '',
    vendorId: '',
    vendorName: '',
    vendorGST: '',
    vendorAddress: '',
    poNumber: '',
    invoiceNo: '',
    ewayBillNo: '',
    billTNo: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    receivedDate: new Date().toISOString().split('T')[0],
    vehicleNo: '',
    driverName: '',
    result: 'Accepted',
    remarks: '',
    status: 'Pending',
    hasImage: false,
    // UOM tracking
    poUom: 'kg',
    isWeightBased: true,
    // Multi-item support
    items: [{
      id: 1,
      commodityId: '',
      commodityName: '',
      hsnCode: '',
      // Weight fields
      grossWeight: '',
      tareWeight: '',
      packingWeight: '',
      netWeight: 0,
      // Quantity fields
      invoiceQty: '',
      receivedQty: '',
      poQty: 0,
      uom: 'kg',
      isWeightBased: true,
      rate: '',
      amount: 0
    }]
  });

  // Load data on mount and when tab changes — reset to page 1 on tab switch.
  // Purchase Orders are loaded lazily when the Add GRN modal is opened (or
  // when a vendor is picked), so this initial mount stays snappy even on
  // large datasets.
  useEffect(() => {
    setGrnPage(1);
    loadGRNList(1);
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnTab]);

  const loadMasterData = async () => {
    setLoadingMasterData(true);
    try {
      // Only load vendors (small dataset)
      // Commodities will be loaded via async search
      const vendorsRes = await axios.get(`${API}/master-data/stores/vendors`);
      setVendors(vendorsRes.data.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
    setLoadingMasterData(false);
  };
  
  // Search commodities (async)
  const searchCommodities = async (searchTerm) => {
    try {
      const response = await axios.get(
        `${API}/master-data/stores/commodities/search?q=${encodeURIComponent(searchTerm)}&limit=50`, 
        
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error searching commodities:', error);
      return [];
    }
  };
  
  const loadPurchaseOrders = async (vendorName = null) => {
    try {
      // Use open-purchase-orders endpoint which excludes fully delivered POs
      let url = `${API}/stores/open-purchase-orders`;
      if (vendorName) {
        url += `?supplier=${encodeURIComponent(vendorName)}`;
      }
      const response = await axios.get(url);
      // Filter by PO type based on active tab
      const allPOs = response.data || [];
      const filteredPOs = allPOs.filter(po => {
        if (grnTab === 'service') return po.poType === 'service';
        return po.poType !== 'service';
      });
      setPurchaseOrders(filteredPOs);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
    }
  };

  const PAGE_LIMIT = 1000;

  // ...rest unchanged below
  const loadGRNList = async (overridePage) => {
    setLoading(true);
    setGrnLoadError(null);
    const targetPage = overridePage || grnPage;
    try {
      const response = await axios.get(`${API}/stores/grn`, {
        params: { poType: grnTab, page: targetPage, limit: PAGE_LIMIT },
        timeout: 60000,
      });
      // Backend now returns { data, total, page, limit, hasMore }
      const payload = response.data || {};
      const list = Array.isArray(payload) ? payload : (payload.data || []);
      setGrnList(list);
      setGrnTotal(Array.isArray(payload) ? list.length : (payload.total || list.length));
      setGrnHasMore(Array.isArray(payload) ? false : !!payload.hasMore);
      setGrnPage(targetPage);
    } catch (error) {
      console.error('Error loading GRN list:', error);
      if (error?.response?.status !== 401) setGrnLoadError(error);
    }
    setLoading(false);
  };

  // Delete GRN function
  const handleDeleteGRN = async (grnId) => {
    try {
      await axios.delete(`${API}/stores/grn/${grnId}`);
      setGrnList(prev => prev.filter(grn => grn.id !== grnId));
      setDeleteConfirm(null);
      setDeleteImpact(null);
    } catch (error) {
      console.error('Error deleting GRN:', error);
      alert('Failed to delete GRN. Please try again.');
    }
  };

  // Fetch impact summary so the confirmation modal can warn about
  // partial consumption, attached QAs/payments, etc.
  const openDeleteConfirm = async (grn) => {
    setDeleteConfirm(grn);
    setDeleteImpact({ loading: true });
    try {
      const res = await axios.get(`${API}/stores/grn/${grn.id}/delete-impact`);
      setDeleteImpact({ loading: false, data: res.data });
    } catch (e) {
      setDeleteImpact({ loading: false, data: null });
    }
  };

  // Open edit modal for a Stores GRN
  const openEditModal = (grn) => {
    setEditData({
      id: grn.id,
      result: grn.result || 'Accepted',
      status: grn.status || 'Pending',
      netWeight: grn.netWeight || grn.quantity || '',
      amount: grn.amount || '',
      remarks: grn.remarks || '',
      vehicleNo: grn.vehicleNo || '',
      invoiceNo: grn.invoiceNo || '',
    });
    setShowEditModal(true);
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Save Stores GRN edit
  const handleSaveGRNEdit = async () => {
    setSavingGRNEdit(true);
    try {
      const payload = {
        result: editData.result,
        status: editData.status,
        remarks: editData.remarks,
      };
      await axios.put(`${API}/stores/grn/${editData.id}`, payload);
      setShowEditModal(false);
      loadGRNList();
    } catch (error) {
      console.error('Error updating GRN:', error);
      alert('Failed to update GRN');
    } finally {
      setSavingGRNEdit(false);
    }
  };

  // GRN number is now generated by the backend (atomic counter)
  // No need to generate on the frontend

  // Calculate net weight and amount for a single item
  const calculateItemValues = (item) => {
    const gross = parseFloat(item.grossWeight) || 0;
    const tare = parseFloat(item.tareWeight) || 0;
    const packing = parseFloat(item.packingWeight) || 0;
    const rate = parseFloat(item.rate) || 0;
    const net = gross - tare - packing;
    const amount = net * rate;
    return { netWeight: net, amount: amount };
  };

  // Handle form input change for non-item fields
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle item field change (for multi-item support)
  const handleItemChange = (itemId, field, value) => {
    setFormData(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-calculate based on UOM type
          if (prev.isWeightBased !== false) {
            // Weight-based calculation
            if (['grossWeight', 'tareWeight', 'packingWeight', 'rate'].includes(field)) {
              const calculated = calculateItemValues(updatedItem);
              updatedItem.netWeight = calculated.netWeight;
              updatedItem.amount = calculated.amount;
            }
          } else {
            // Quantity-based calculation
            if (['receivedQty', 'rate'].includes(field)) {
              const qty = parseFloat(updatedItem.receivedQty) || 0;
              const rate = parseFloat(updatedItem.rate) || 0;
              updatedItem.netWeight = qty; // Use qty as netWeight for consistency
              updatedItem.amount = qty * rate;
            }
          }
          
          return updatedItem;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  };

  // Add new item row
  const addItemRow = () => {
    const newId = Math.max(...formData.items.map(i => i.id)) + 1;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: newId,
        commodityId: '',
        commodityName: '',
        hsnCode: '',
        grossWeight: '',
        tareWeight: '',
        packingWeight: '',
        netWeight: 0,
        // Quantity fields (for qty-based POs)
        invoiceQty: '',
        receivedQty: '',
        poQty: 0,
        uom: prev.poUom || 'kg',
        isWeightBased: prev.isWeightBased !== false,
        rate: '',
        amount: 0
      }]
    }));
  };

  // ----- 1A+2A constraint helpers -----
  // For a given row, return PO commodities EXCLUDING those already chosen
  // in other rows (keeps the row's own selection visible).
  const getAvailableCommoditiesFor = (rowId) => {
    if (!poCommodities || poCommodities.length === 0) return [];
    const usedIds = formData.items
      .filter(i => i.id !== rowId)
      .map(i => i.commodityId)
      .filter(Boolean);
    return poCommodities.filter(c => !usedIds.includes(c.id));
  };

  // True when every PO commodity is already referenced by some row → Add Item disabled
  const allPoItemsUsed = (
    poCommodities.length > 0 &&
    poCommodities.every(c => formData.items.some(i => i.commodityId === c.id))
  );

  // Remove item row
  const removeItemRow = (itemId) => {
    if (formData.items.length <= 1) return; // Keep at least one item
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  // Handle commodity selection for an item
  const handleItemCommoditySelect = (itemId, commodityId, commodity) => {
    setFormData(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          // For "Add Item" rows that didn't have a rate/uom yet, hydrate from
          // the PO commodity so behavior matches the auto-populated initial rows.
          const needsHydration = !item.rate && !item.uom;
          return {
            ...item,
            commodityId: commodityId,
            commodityName: commodity.commodityName || '',
            hsnCode: commodity.hsnCode || '',
            // Hydrate PO metadata on first selection (does NOT overwrite a
            // rate the user has manually adjusted)
            rate: item.rate || commodity.rate || '',
            uom: item.uom || commodity.unit || prev.poUom || 'kg',
            poQty: item.poQty || commodity.quantity || 0,
            ...(needsHydration ? { isWeightBased: prev.isWeightBased } : {}),
          };
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  };

  // Calculate GRN totals
  const grnTotals = formData.items.reduce((acc, item) => ({
    totalNetWeight: acc.totalNetWeight + (parseFloat(item.netWeight) || 0),
    totalAmount: acc.totalAmount + (parseFloat(item.amount) || 0)
  }), { totalNetWeight: 0, totalAmount: 0 });
  
  // Handle vendor selection
  const handleVendorSelect = (vendorId, vendor) => {
    const vendorName = vendor.vendorName || '';
    setFormData({
      ...formData,
      vendorId: vendorId,
      vendorName: vendorName,
      vendorGST: vendor.gstin || vendor.gstNo || '',
      vendorAddress: `${vendor.address || ''}, ${vendor.city || ''}, ${vendor.state || ''}`.replace(/^, |, $/g, '').replace(/, ,/g, ','),
      poNumber: '' // Reset PO when vendor changes
    });
    // Reload POs for this vendor (excluding fully delivered)
    loadPurchaseOrders(vendorName);
    // Reset PO selection when vendor changes
    setSelectedPO(null);
    setSelectedItemKey('');
    setPoCommodities([]);
    setPoMiscCharges([]);
  };
  
  // Handle PO selection - extract commodities from PO
  const handlePOSelect = (itemKey, po) => {
    const poNumber = po.poNumber || po.id;
    setSelectedItemKey(itemKey);
    setFormData(prev => ({ ...prev, poNumber: poNumber }));
    setSelectedPO(po);
    
    // Extract miscellaneous charges from PO
    setPoMiscCharges(po.miscCharges || []);
    
    // The /open-purchase-orders endpoint returns ONE dropdown entry per
    // PO-item combo so users can search by item name. The item identity is
    // intentionally unique per line (po + commodity + item index), so we must
    // seed the GRN rows from that exact selected line instead of collapsing by
    // commodity name across multiple PO lines.
    const pickedItems = (po.items || []).filter(item => {
      if (!itemKey) return true;
      return (item._itemKey || item.itemKey || item.poItemKey) === itemKey;
    });
    const allPoItems = (Array.isArray(po.allItems) && po.allItems.length > 0)
      ? po.allItems
      : pickedItems;
    
    // Determine if this is a weight-based or quantity-based PO
    const poUom = po?.uom || (allPoItems?.[0]?.uom || allPoItems?.[0]?.unit || 'kg');
    const isWeightBased = ['kg', 'KG', 'KGS', 'kgs', 'MT', 'mt', 'g', 'gm', 'gram'].includes(poUom);
    
    if (allPoItems.length > 0) {
      // Master list for the dropdown — ALL PO items so user can pick others
      // with + Add Item.
      const allCommodities = allPoItems.map((item, index) => {
        const commodityName = item.name || item.commodityName || item.material || '';
        const itemKey = item._itemKey || item.itemKey || item.poItemKey || `${poNumber}__${commodityName.toUpperCase().trim()}__${index}`;
        return {
          id: `po-item-${index}`,
          commodityName,
          _itemKey: itemKey,
          poItemKey: itemKey,
          hsnCode: item.hsnCode || '',
          rate: item.rate || 0,
          // CRITICAL: carry the PO's gstRate into the GRN line. The PO's
          // GST is the source of truth for payments (user policy). Without
          // this, the backend falls back to master data and overrides any
          // negotiated rate (e.g. SAC 996511 master=5% but user negotiated
          // 18% with this vendor).
          gstRate: item.gstRate ?? item.gst ?? '',
          quantity: item.quantity || 0,
          unit: item.unit || item.uom || poUom
        };
      });
      setPoCommodities(allCommodities);
      
      // Initial rows — ONLY the picked items (so user isn't forced to fill
      // qty/weight for every item on the PO when receiving a partial GRN).
      const selectedItemIds = new Set(
        pickedItems
          .map(p => (p._itemKey || p.itemKey || p.poItemKey || '').trim())
          .filter(Boolean)
      );
      const initialCommodities = selectedItemIds.size > 0
        ? allCommodities.filter(c => selectedItemIds.has(c._itemKey || c.itemKey || c.poItemKey || ''))
        : allCommodities;  // fallback: if we can't match, show all
      
      const grnItems = (initialCommodities.length > 0 ? initialCommodities : allCommodities).map((comm, idx) => ({
        id: idx + 1,
        commodityId: comm.id,
        commodityName: comm.commodityName,
        hsnCode: comm.hsnCode,
        // Carry PO's gstRate forward — payment computation uses this as the
        // authoritative rate (PO > master).
        gstRate: comm.gstRate ?? '',
        // Weight fields (for weight-based POs)
        grossWeight: '',
        tareWeight: '',
        packingWeight: '',
        netWeight: 0,
        // Quantity fields (for quantity-based POs)
        invoiceQty: '',
        receivedQty: '',
        poQty: comm.quantity || 0,
        uom: comm.unit || poUom,
        isWeightBased: isWeightBased,
        rate: comm.rate || '',
        amount: 0
      }));
      
      setFormData(prev => ({
        ...prev,
        poNumber: poNumber,
        poUom: poUom,
        isWeightBased: isWeightBased,
        items: grnItems.length > 0 ? grnItems : prev.items
      }));
    } else {
      setPoCommodities([]);
    }
  };
  
  // Get POs for selected vendor (already filtered from API, excluding fully delivered)
  const vendorPOs = purchaseOrders.filter(po => 
    po.supplier?.toLowerCase() === formData.vendorName?.toLowerCase()
  );

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      console.error('Camera access error:', err);
      alert('Unable to access camera. Please ensure permissions are granted.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsStreaming(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      setCapturedImages(prev => [...prev, canvas.toDataURL('image/jpeg', 0.9)]);
      stopCamera();
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setCapturedImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  // Save new GRN (multi-item support)
  const handleSaveGRN = async () => {
    if (saveStatus === 'saving') return;

    const requestKey = submitKeyRef.current || (
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    );
    submitKeyRef.current = requestKey;
    setSaveStatus('saving');
    
    const grnData = {
      // ID will be generated by backend (atomic counter)
      idempotencyKey: requestKey,
      vendorId: formData.vendorId,
      vendorName: formData.vendorName,
      supplier: formData.vendorName,
      vendorGST: formData.vendorGST,
      vendorAddress: formData.vendorAddress,
      poNumber: formData.poNumber,
      invoiceNo: formData.invoiceNo,
      ewayBillNo: formData.ewayBillNo,
      billTNo: formData.billTNo,
      invoiceDate: formData.invoiceDate,
      receivedDate: formData.receivedDate,
      date: formData.receivedDate,
      vehicleNo: formData.vehicleNo,
      driverName: formData.driverName,
      result: formData.result,
      remarks: formData.remarks,
      status: formData.status,
      hasImage: capturedImages.length > 0,
      // Multi-item data
      items: formData.items.map(item => ({
        commodityId: item.commodityId,
        commodityName: item.commodityName,
        material: item.commodityName,
        hsnCode: item.hsnCode,
        uom: item.uom || formData.poUom || 'kg',
        // Weight fields
        grossWeight: parseFloat(item.grossWeight) || 0,
        tareWeight: parseFloat(item.tareWeight) || 0,
        packingWeight: parseFloat(item.packingWeight) || 0,
        netWeight: parseFloat(item.netWeight) || 0,
        // Quantity fields
        invoiceQty: parseFloat(item.invoiceQty) || 0,
        receivedQty: parseFloat(item.receivedQty) || 0,
        poQty: parseFloat(item.poQty) || 0,
        rate: parseFloat(item.rate) || 0,
        amount: parseFloat(item.amount) || 0
      })),
      // Summary fields for backwards compatibility
      quantity: grnTotals.totalNetWeight,
      netWeight: grnTotals.totalNetWeight,
      amount: grnTotals.totalAmount,
      unit: formData.poUom || selectedPO?.uom || 'kg',
      uom: formData.poUom || selectedPO?.uom || 'kg',
      isWeightBased: formData.isWeightBased,
      material: formData.items.map(i => i.commodityName).join(', '),
      itemCount: formData.items.length,
      miscCharges: poMiscCharges || [],
      createdBy: user?.name || '',
      createdAt: new Date().toISOString(),
      images: capturedImages || [],
    };

    try {
      await axios.post(`${API}/stores/grn`, grnData);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        loadGRNList();
        resetForm();
      }, 1500);
    } catch (error) {
      console.error('Error saving GRN:', error);
      submitKeyRef.current = null;
      const errMsg = error.response?.data?.detail || error.message || 'Failed to save GRN';
      const displayMsg = typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg);
      alert('Error: ' + displayMsg);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };
  
  const resetForm = () => {
    submitKeyRef.current = null;
    setFormData({
      grnNumber: '',
      vendorId: '',
      vendorName: '',
      vendorGST: '',
      vendorAddress: '',
      poNumber: '',
      invoiceNo: '',
      ewayBillNo: '',
      billTNo: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      receivedDate: new Date().toISOString().split('T')[0],
      vehicleNo: '',
      driverName: '',
      result: 'Accepted',
      remarks: '',
      status: 'Pending',
      hasImage: false,
      items: [{
        id: 1,
        commodityId: '',
        commodityName: '',
        hsnCode: '',
        grossWeight: '',
        tareWeight: '',
        packingWeight: '',
        netWeight: 0,
        rate: '',
        amount: 0
      }]
    });
    setSelectedPO(null);
    setSelectedItemKey('');
    setPoCommodities([]);
    setPoMiscCharges([]);
    setCapturedImages([]);
    stopCamera();
  };

  // Filter GRN list
  const filteredList = grnList.filter(grn => {
    const matchesSearch = 
      grn.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.material?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grn.poNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'All' || grn.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Get result color
  const getResultColor = (result) => {
    if (result === 'Shortage') {
      return 'bg-red-100 text-red-700 border-red-200 font-bold animate-pulse';
    }
    return 'bg-green-100 text-green-700 border-green-200';
  };

  // Summary stats
  const totalGRN = grnList.length;
  const pendingGRN = grnList.filter(g => g.status === 'Pending' || g.status === 'Pending QA').length;
  const verifiedGRN = grnList.filter(g => g.status === 'Verified').length;
  const shortageGRN = grnList.filter(g => g.result === 'Shortage').length;

  return (
    <div className="space-y-4" data-testid="stores-grn-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Stores GRN - Goods Receipt Note</h1>
            <p className="text-sm text-gray-500">Record incoming materials with vendor & commodity from Master Data</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search GRN, Invoice, Vendor..."
              className="bg-transparent border-none outline-none text-sm w-48"
              data-testid="search-input"
            />
          </div>

          {/* Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-amber-400"
            data-testid="status-filter"
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Pending QA">Pending QA</option>
            <option value="Verified">Verified</option>
            <option value="Processing">Processing</option>
          </select>

          {/* Refresh */}
          <button
            onClick={() => { loadGRNList(); loadMasterData(); }}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>

          {/* View Deleted GRNs */}
          <button
            onClick={() => navigate('/stores/grn/deleted')}
            className="flex items-center gap-2 px-3 py-2 border border-rose-200 text-rose-700 bg-rose-50 rounded-lg hover:bg-rose-100 transition text-sm font-medium"
            data-testid="view-deleted-grns-btn"
            title="View audit log of every deleted GRN"
          >
            <Trash2 size={14} />
            <span>Deleted GRNs</span>
          </button>

          {/* Add New GRN */}
          <button
            onClick={() => {
              setShowAddModal(true);
              loadPurchaseOrders();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition text-sm font-medium"
            data-testid="add-grn-btn"
          >
            <Plus size={16} />
            Create GRN
          </button>
        </div>
      </div>

      {/* Material GRN / Service GRN Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => { setGrnTab('material'); setShowAddModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              grnTab === 'material'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="material-grn-tab"
          >
            <Package size={18} />
            Material GRN
          </button>
          <button
            onClick={() => { setGrnTab('service'); setShowAddModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              grnTab === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="service-grn-tab"
          >
            <Briefcase size={18} />
            Service GRN
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total GRN</p>
          <p className="text-2xl font-bold">{totalGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Pending</p>
          <p className="text-2xl font-bold">{pendingGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Verified</p>
          <p className="text-2xl font-bold">{verifiedGRN}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1">
            <AlertTriangle size={14} />
            <p className="text-xs opacity-80">Shortage</p>
          </div>
          <p className="text-2xl font-bold">{shortageGRN}</p>
        </div>
      </div>

      {grnLoadError && grnList.length > 0 ? (
        <div
          className="mb-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-1.5 text-xs text-amber-900 flex items-center gap-2"
          data-testid="list-load-stale-banner"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          <span className="flex-1">Couldn&apos;t refresh — showing last successful fetch ({grnList.length} record{grnList.length === 1 ? '' : 's'}).</span>
          <button
            onClick={() => loadGRNList()}
            disabled={loading}
            className="px-2 py-0.5 bg-amber-600 text-white rounded text-[11px] font-medium hover:bg-amber-700 disabled:opacity-60"
          >
            {loading ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      ) : (
        <ListLoadError
          error={grnLoadError}
          busy={loading}
          onRetry={() => loadGRNList()}
          label="GRN records"
        />
      )}

      {/* GRN Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <Package size={16} className="text-amber-500" />
            GRN List - Stores Department
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">GRN #</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">PO Number</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Invoice No</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Vendor</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Commodity</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Items</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Received Qty</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Result</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Photo</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    No GRN records found
                  </td>
                </tr>
              ) : (
                filteredList.map((grn) => (
                  <tr key={grn.id} className="hover:bg-amber-50/50 transition" data-testid={`grn-row-${grn.id}`}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-amber-600 font-medium">{grn.id}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{grn.poNumber || '-'}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-blue-600">{grn.invoiceNo}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{grn.date || grn.receivedDate}</td>
                    <td className="px-3 py-2 font-medium">{grn.supplier || grn.vendorName}</td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700">{grn.material || grn.commodityName}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        (grn.itemCount || grn.items?.length || 1) > 1 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {grn.itemCount || grn.items?.length || 1}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-blue-600">
                      {(() => {
                        const qty = grn.netWeight || grn.quantity;
                        if (qty) return <>{qty.toLocaleString()} <span className="text-[10px] text-gray-500 font-normal">{grn.uom || grn.unit || 'kg'}</span></>;
                        const itemQty = grn.items?.[0]?.receivedQty || grn.items?.reduce((sum, i) => sum + (i.receivedQty || 0), 0);
                        return <>{itemQty || 0} <span className="text-[10px] text-gray-500 font-normal">{grn.items?.[0]?.uom || grn.uom || grn.unit || 'kg'}</span></>;
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getResultColor(grn.result)}`}>
                        {grn.result === 'Shortage' && <AlertTriangle size={10} className="inline mr-1" />}
                        {grn.result || 'Accepted'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(grn.hasImage || grn.images?.length > 0) ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); setImageViewerGRN(grn); }}
                          className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                          title="View photos"
                        >
                          <Eye size={14} />
                        </button>
                      ) : (
                        <span className="text-gray-400 text-[10px]">No image</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        grn.status === 'Verified' ? 'bg-green-100 text-green-700' :
                        grn.status === 'Pending QA' ? 'bg-orange-100 text-orange-700' :
                        grn.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {grn.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedGRN(grn);
                            setShowViewModal(true);
                          }}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded transition"
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(grn)}
                            className="p-1 text-amber-500 hover:bg-amber-100 rounded transition"
                            title="Edit GRN"
                            data-testid={`edit-grn-${grn.id}`}
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => openDeleteConfirm(grn)}
                            className="p-1 text-red-500 hover:bg-red-100 rounded transition"
                            title="Delete GRN"
                            data-testid={`delete-grn-${grn.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {grnTotal > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs" data-testid="grn-pagination">
            <div className="text-gray-600">
              Showing <span className="font-semibold">{(grnPage - 1) * PAGE_LIMIT + 1}</span>–
              <span className="font-semibold">{Math.min(grnPage * PAGE_LIMIT, grnTotal)}</span> of{' '}
              <span className="font-semibold">{grnTotal}</span> GRN record(s)
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => grnPage > 1 && loadGRNList(grnPage - 1)}
                disabled={grnPage <= 1 || loading}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="grn-prev-page"
              >
                ← Prev
              </button>
              <span className="px-2 text-gray-500">Page {grnPage}</span>
              <button
                onClick={() => grnHasMore && loadGRNList(grnPage + 1)}
                disabled={!grnHasMore || loading}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="grn-next-page"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add GRN Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <FileText className="text-amber-500" size={20} />
                <h2 className="font-bold text-lg">Create New {grnTab === 'service' ? 'Service ' : ''}GRN - Stores</h2>
              </div>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* GRN & Invoice Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GRN Number</label>
                  <input
                    type="text"
                    value={formData.grnNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.invoiceNo}
                    onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
                    placeholder="INV-XXX-2026-0001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="invoice-no-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Eway Bill No</label>
                  <input
                    type="text"
                    value={formData.ewayBillNo}
                    onChange={(e) => handleInputChange('ewayBillNo', e.target.value)}
                    placeholder="EWAY-XXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="eway-bill-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bill T No</label>
                  <input
                    type="text"
                    value={formData.billTNo}
                    onChange={(e) => handleInputChange('billTNo', e.target.value)}
                    placeholder="BILLT-XXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    data-testid="bill-t-no-input"
                  />
                </div>
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Received Date</label>
                  <input
                    type="date"
                    value={formData.receivedDate}
                    onChange={(e) => handleInputChange('receivedDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                  />
                </div>
              </div>

              {/* Vendor & PO Selection from Master Data */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Package size={16} className="text-amber-500" />
                  Vendor & Purchase Order
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Vendor Dropdown */}
                  <SearchableDropdown
                    label="Vendor"
                    value={formData.vendorId}
                    options={vendors.filter(v => !v.status || v.status === 'Active')}
                    onChange={handleVendorSelect}
                    placeholder="Search and select vendor..."
                    displayField="vendorName"
                    valueField="id"
                    required={true}
                    testId="vendor-dropdown"
                  />
                  
                  {/* PO Selection */}
                  <SearchableDropdown
                    label="PO Number"
                    value={selectedItemKey}
                    options={vendorPOs.map(po => ({ 
                      ...po, 
                      displayText: po.displayText || `${po.id} - Remaining: ${(po.remainingQty || 0).toLocaleString()} kg`
                    }))}
                    onChange={(value, po) => handlePOSelect(value, po)}
                    placeholder={formData.vendorName ? "Select open PO..." : "Select vendor first..."}
                    displayField="displayText"
                    valueField="_itemKey"
                    required={true}
                    disabled={!formData.vendorName}
                    testId="po-dropdown"
                  />
                </div>
                
                {/* Auto-filled from selection */}
                {formData.vendorName && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm">
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-gray-500 text-xs">Vendor GST:</span>
                        <span className="ml-2 font-medium">{formData.vendorGST || '-'}</span>
                      </div>
                      {vendorPOs.length === 0 && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <span>✓</span> All POs for {formData.vendorName} are fully delivered
                        </p>
                      )}
                      {selectedPO && selectedPO.items && (
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          <span>📦</span> PO has {poCommodities.length || selectedPO.items?.length || 0} item(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Multi-Item Commodity Entry Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Package size={16} className="text-purple-500" />
                    Commodity Items ({formData.items.length})
                    {selectedPO && (
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        (UOM: {formData.poUom || selectedPO?.uom || 'kg'})
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={addItemRow}
                    disabled={allPoItemsUsed}
                    title={allPoItemsUsed ? 'All PO items already added' : 'Add another commodity row'}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition text-sm font-medium ${
                      allPoItemsUsed
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                    data-testid="add-item-btn"
                  >
                    <Plus size={14} />
                    Add Item
                  </button>
                </div>
                {allPoItemsUsed && (
                  <div className="text-[11px] text-gray-500 mb-2 italic">
                    All commodities from this PO have been added. Off-PO items are not allowed.
                  </div>
                )}
                
                {/* Items Table - Dynamic based on UOM type */}
                <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700 w-8">#</th>
                          <th className="px-2 py-2 text-left font-semibold text-gray-700 min-w-[200px]">{grnTab === 'service' ? 'Service (SAC)' : 'Commodity (HSN)'}</th>
                          {formData.isWeightBased !== false ? (
                            <>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Gross (kg)</th>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Tare (kg)</th>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Packing (kg)</th>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Net (kg)</th>
                            </>
                          ) : (
                            <>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">PO Qty</th>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Invoice Qty</th>
                              <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Received Qty</th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 w-24">Status</th>
                            </>
                          )}
                          <th className="px-2 py-2 text-right font-semibold text-gray-700 w-24">Rate (₹)</th>
                          <th className="px-2 py-2 text-right font-semibold text-gray-700 w-28">Amount (₹)</th>
                          <th className="px-2 py-2 text-center font-semibold text-gray-700 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {formData.items.map((item, index) => (
                          <tr key={item.id} className="bg-white">
                            <td className="px-2 py-2 text-gray-500 font-medium">{index + 1}</td>
                            <td className="px-2 py-2">
                              {poCommodities.length > 0 ? (
                                /* Use PO commodities when a PO is selected -
                                   filter out items chosen in OTHER rows (1A+2A) */
                                <SearchableDropdown
                                  label=""
                                  value={item.commodityId}
                                  options={getAvailableCommoditiesFor(item.id)}
                                  onChange={(id, commodity) => handleItemCommoditySelect(item.id, id, commodity)}
                                  placeholder="Select commodity from PO..."
                                  displayField="commodityName"
                                  valueField="id"
                                  required={true}
                                  testId={`commodity-dropdown-${item.id}`}
                                />
                              ) : (
                                /* Fallback to async search if no PO selected */
                                <AsyncSearchableDropdown
                                  label=""
                                  value={item.commodityId}
                                  selectedDisplay={item.hsnCode ? `${item.hsnCode} - ${item.commodityName}` : (item.commodityName || '')}
                                  onSearch={searchCommodities}
                                  onChange={(id, commodity) => handleItemCommoditySelect(item.id, id, commodity)}
                                  placeholder="Select PO first..."
                                  displayField="commodityName"
                                  valueField="id"
                                  required={true}
                                  disabled={!formData.poNumber}
                                  testId={`commodity-dropdown-${item.id}`}
                                />
                              )}
                            </td>
                            {formData.isWeightBased !== false ? (
                              <>
                                {/* Weight-based fields */}
                                <td className="px-2 py-2">
                                  <input
                                    type="number"
                                    value={item.grossWeight}
                                    onChange={(e) => handleItemChange(item.id, 'grossWeight', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                    data-testid={`gross-weight-${item.id}`}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="number"
                                    value={item.tareWeight}
                                    onChange={(e) => handleItemChange(item.id, 'tareWeight', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                    data-testid={`tare-weight-${item.id}`}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="number"
                                    value={item.packingWeight}
                                    onChange={(e) => handleItemChange(item.id, 'packingWeight', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <div className="px-2 py-1.5 bg-blue-50 rounded text-sm text-right font-bold text-blue-700">
                                    {item.netWeight?.toLocaleString() || 0}
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                {/* Quantity-based fields */}
                                <td className="px-2 py-2">
                                  <div className="px-2 py-1.5 bg-gray-100 rounded text-sm text-right text-gray-600">
                                    {item.poQty || 0} {item.uom}
                                  </div>
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="number"
                                    value={item.invoiceQty}
                                    onChange={(e) => handleItemChange(item.id, 'invoiceQty', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                    data-testid={`invoice-qty-${item.id}`}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="number"
                                    value={item.receivedQty}
                                    onChange={(e) => {
                                      handleItemChange(item.id, 'receivedQty', e.target.value);
                                      // Auto-calculate netWeight for quantity-based items
                                      const qty = parseFloat(e.target.value) || 0;
                                      handleItemChange(item.id, 'netWeight', qty);
                                    }}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:border-amber-400 outline-none"
                                    data-testid={`received-qty-${item.id}`}
                                  />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  {item.invoiceQty && item.receivedQty && (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      parseFloat(item.receivedQty) >= parseFloat(item.invoiceQty) 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {parseFloat(item.receivedQty) >= parseFloat(item.invoiceQty) ? 'Full' : 'Short'}
                                    </span>
                                  )}
                                </td>
                              </>
                            )}
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.rate}
                                readOnly
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right bg-gray-50 text-gray-700 cursor-not-allowed"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <div className="px-2 py-1.5 bg-green-50 rounded text-sm text-right font-bold text-green-700">
                                {item.amount?.toLocaleString() || 0}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-center">
                              {formData.items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItemRow(item.id)}
                                  className="p-1 text-red-500 hover:bg-red-100 rounded transition"
                                  title="Remove item"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                        <tr>
                          <td colSpan={5} className="px-2 py-2 text-right font-bold text-gray-700">
                            GRN Totals:
                          </td>
                          <td className="px-2 py-2">
                            <div className="px-2 py-1.5 bg-blue-100 rounded text-sm text-right font-bold text-blue-800">
                              {grnTotals.totalNetWeight.toLocaleString()}
                            </div>
                          </td>
                          <td className="px-2 py-2"></td>
                          <td className="px-2 py-2">
                            <div className="px-2 py-1.5 bg-green-100 rounded text-sm text-right font-bold text-green-800">
                              ₹{grnTotals.totalAmount.toLocaleString()}
                            </div>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              {/* Miscellaneous Charges from PO (Read-only) */}
              {poMiscCharges.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-amber-500" />
                    Miscellaneous Charges (from PO - Non-GST)
                  </h3>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <div className="space-y-2">
                      {poMiscCharges.map((ch, idx) => (
                        <div key={idx} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-amber-100">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400 font-mono">{idx + 1}.</span>
                            <span className="text-sm text-gray-700" data-testid={`grn-misc-charge-${idx}`}>{ch.description}</span>
                          </div>
                          <span className="text-sm font-semibold text-amber-700">
                            ₹{(parseFloat(ch.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-100 rounded-lg font-semibold">
                        <span className="text-sm text-amber-800">Total Misc. Charges</span>
                        <span className="text-sm text-amber-800">
                          ₹{poMiscCharges.reduce((sum, ch) => sum + (parseFloat(ch.amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">* These charges are auto-fetched from the linked Purchase Order and cannot be modified here.</p>
                  </div>
                </div>
              )}

              {/* Vehicle Details */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input
                      type="text"
                      value={formData.vehicleNo}
                      onChange={(e) => handleInputChange('vehicleNo', e.target.value)}
                      placeholder="MH-04-AB-1234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Driver Name</label>
                    <input
                      type="text"
                      value={formData.driverName}
                      onChange={(e) => handleInputChange('driverName', e.target.value)}
                      placeholder="Driver name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    />
                  </div>
                </div>
              </div>
              
              {/* Result */}
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Result <span className="text-red-500">*</span></label>
                    <select
                      value={formData.result}
                      onChange={(e) => handleInputChange('result', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none font-medium ${
                        formData.result === 'Accepted' 
                          ? 'border-green-400 bg-green-50 text-green-700' 
                          : 'border-red-400 bg-red-50 text-red-700'
                      }`}
                      data-testid="result-select"
                    >
                      <option value="Accepted">Accepted</option>
                      <option value="Shortage">Shortage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                    <input
                      type="text"
                      value={formData.remarks}
                      onChange={(e) => handleInputChange('remarks', e.target.value)}
                      placeholder="Any additional notes..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Camera Section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                  <Camera size={18} className="text-amber-500" />
                  <span>GRN Photo / Documents ({capturedImages.length})</span>
                </h3>
                
                {/* Uploaded images grid */}
                {capturedImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {capturedImages.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setCapturedImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                          data-testid={`remove-image-${idx}`}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Camera preview */}
                {isStreaming && (
                  <div className="bg-gray-900 rounded-xl aspect-video relative overflow-hidden mb-3">
                    <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                  </div>
                )}
                
                {capturedImages.length === 0 && !isStreaming && (
                  <div className="bg-gray-100 rounded-xl aspect-video flex flex-col items-center justify-center text-gray-400 mb-3">
                    <Camera size={48} />
                    <p className="mt-2 text-sm">Take or upload photos / documents</p>
                  </div>
                )}
                <canvas ref={canvasRef} className="hidden" />

                <div className="flex flex-wrap gap-2">
                  {!isStreaming && (
                    <>
                      <button onClick={startCamera} className="flex-1 bg-amber-500 text-white py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-amber-600 transition" data-testid="start-camera-btn">
                        <Camera size={18} /><span>Camera</span>
                      </button>
                      <button onClick={() => fileInputRef.current?.click()} className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-gray-200 transition" data-testid="upload-files-btn">
                        <Upload size={18} /><span>Upload Files</span>
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" multiple onChange={handleFileUpload} className="hidden" />
                    </>
                  )}
                  {isStreaming && (
                    <>
                      <button onClick={capturePhoto} className="flex-1 bg-green-600 text-white py-2.5 px-4 rounded-xl font-medium" data-testid="capture-photo-btn">Capture</button>
                      <button onClick={stopCamera} className="bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium">Cancel</button>
                    </>
                  )}
                  {capturedImages.length > 0 && !isStreaming && (
                    <button onClick={() => setCapturedImages([])} className="bg-red-50 text-red-600 py-2.5 px-4 rounded-xl font-medium text-sm" data-testid="clear-all-images-btn">Clear All</button>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGRN}
                disabled={saveStatus === 'saving' || !formData.invoiceNo || !formData.vendorId || !formData.items.some(item => item.commodityId) || !formData.poNumber}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  saveStatus === 'saved' 
                    ? 'bg-green-500 text-white' 
                    : saveStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="save-grn-btn"
              >
                {saveStatus === 'saving' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Saving...
                  </>
                ) : saveStatus === 'saved' ? (
                  <>
                    <CheckCircle size={16} />
                    Saved!
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <AlertCircle size={16} />
                    Error
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Create GRN
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View GRN Modal */}
      {showViewModal && selectedGRN && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="text-blue-500" size={20} />
                <h2 className="font-bold text-lg">GRN Details - {selectedGRN.id}</h2>
                {(selectedGRN.itemCount || selectedGRN.items?.length || 1) > 1 && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    {selectedGRN.itemCount || selectedGRN.items?.length} Items
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status & Result Badges */}
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedGRN.status === 'Verified' ? 'bg-green-100 text-green-700' :
                    selectedGRN.status === 'Pending QA' ? 'bg-orange-100 text-orange-700' :
                    selectedGRN.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedGRN.status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getResultColor(selectedGRN.result)}`}>
                    {selectedGRN.result === 'Shortage' && <AlertTriangle size={12} className="inline mr-1" />}
                    {selectedGRN.result || 'Accepted'}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  Date: {selectedGRN.date || selectedGRN.receivedDate}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">PO Number</p>
                  <p className="font-medium">{selectedGRN.poNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Invoice Number</p>
                  <p className="font-medium text-blue-600">{selectedGRN.invoiceNo}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Eway Bill No</p>
                  <p className="font-medium">{selectedGRN.ewayBillNo || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Bill T No</p>
                  <p className="font-medium">{selectedGRN.billTNo || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Vendor</p>
                  <p className="font-medium">{selectedGRN.supplier || selectedGRN.vendorName}</p>
                </div>
              </div>

              {/* Items Table (for multi-item GRNs) */}
              {selectedGRN.items && selectedGRN.items.length > 0 ? (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Package size={16} className="text-purple-500" />
                    Commodity Items ({selectedGRN.items.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">#</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Commodity</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">{grnTab === 'service' ? 'SAC' : 'HSN'}</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">UOM</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Received Qty</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Rate (₹)</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedGRN.items.map((item, idx) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium">{item.commodityName || item.material}</td>
                            <td className="px-3 py-2 text-gray-600">{item.hsnCode || '-'}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{item.uom || item.unit || 'kg'}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-600">{item.netWeight?.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right">{item.rate?.toLocaleString() || '-'}</td>
                            <td className="px-3 py-2 text-right font-bold text-green-600">₹{item.amount?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 text-right font-bold text-gray-700">Totals:</td>
                          <td className="px-3 py-2 text-right font-bold text-blue-700">{(selectedGRN.netWeight || selectedGRN.quantity)?.toLocaleString()} {selectedGRN.uom || selectedGRN.unit || 'kg'}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right font-bold text-green-700">₹{selectedGRN.amount?.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ) : (
                /* Legacy single-item display */
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Commodity & Weight</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="col-span-2">
                        <p className="text-gray-500 text-xs">Commodity</p>
                        <p className="font-medium">{selectedGRN.material || selectedGRN.commodityName}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Received Qty</p>
                        <p className="font-bold text-blue-600 text-lg">{(selectedGRN.netWeight || selectedGRN.quantity)?.toLocaleString()} {selectedGRN.uom || selectedGRN.unit || 'kg'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Amount</p>
                        <p className="font-bold text-green-600 text-lg">₹{selectedGRN.amount?.toLocaleString() || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedGRN.remarks && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-500 text-xs">Remarks</p>
                  <p className="text-sm">{selectedGRN.remarks}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-full ${deleteImpact?.data?.hasConsumption ? 'bg-amber-100' : 'bg-red-100'}`}>
                  <AlertTriangle className={deleteImpact?.data?.hasConsumption ? 'text-amber-600' : 'text-red-600'} size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  {deleteImpact?.data?.hasConsumption ? 'Delete GRN — material already consumed' : 'Delete GRN'}
                </h3>
              </div>
              <p className="text-gray-600 mb-2">Are you sure you want to delete this GRN?</p>
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm"><span className="text-gray-500">GRN No:</span> <span className="font-mono font-medium">{deleteConfirm.id}</span></p>
                <p className="text-sm"><span className="text-gray-500">Invoice:</span> <span className="font-medium">{deleteConfirm.invoiceNo}</span></p>
                <p className="text-sm"><span className="text-gray-500">Vendor:</span> {deleteConfirm.supplier || deleteConfirm.vendorName}</p>
              </div>

              {/* Cascade impact preview */}
              {deleteImpact?.loading && (
                <div className="text-xs text-gray-500 italic mb-3">Loading impact summary…</div>
              )}
              {deleteImpact?.data && (
                <div className="mb-3" data-testid="delete-impact-summary">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Cascade impact (will be removed):
                  </p>
                  <ul className="text-xs text-gray-600 space-y-0.5 pl-4 list-disc">
                    <li>{deleteImpact.data.qaCount} QA record{deleteImpact.data.qaCount === 1 ? '' : 's'}</li>
                    <li>{deleteImpact.data.lotsCount} stock lot{deleteImpact.data.lotsCount === 1 ? '' : 's'}</li>
                    <li>{deleteImpact.data.paymentsCount} payment{deleteImpact.data.paymentsCount === 1 ? '' : 's'}</li>
                  </ul>

                  {deleteImpact.data.materials.length > 0 && (
                    <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-amber-800 mb-2">Stock impact per material:</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-amber-700 border-b border-amber-200">
                            <th className="text-left font-medium py-1">Material</th>
                            <th className="text-right font-medium py-1">Received</th>
                            <th className="text-right font-medium py-1">Remaining</th>
                            <th className="text-right font-medium py-1">Consumed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deleteImpact.data.materials.map((m) => (
                            <tr key={m.materialName} className="border-b border-amber-100 last:border-0">
                              <td className="py-1 text-gray-900">{m.materialName}</td>
                              <td className="py-1 text-right">{m.totalReceived}</td>
                              <td className="py-1 text-right">{m.totalRemaining}</td>
                              <td className={`py-1 text-right font-semibold ${m.totalConsumed > 0 ? 'text-rose-700' : 'text-gray-500'}`}>
                                {m.totalConsumed}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {deleteImpact.data.hasConsumption && (
                    <p className="mt-3 text-sm text-amber-700 bg-amber-100 border border-amber-300 rounded p-2 flex items-start gap-2" data-testid="consumption-warning">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>
                        <b>{deleteImpact.data.totalConsumed}</b> {deleteImpact.data.materials[0]?.unit || 'unit(s)'} have already been issued/consumed.
                        Deleting this GRN will still remove its remaining stock but the consumed history will stay in your issue records.
                      </span>
                    </p>
                  )}
                </div>
              )}

              <p className="text-red-600 text-sm">This action cannot be undone.</p>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteImpact(null); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGRN(deleteConfirm.id)}
                disabled={deleteImpact?.loading}
                className={`px-4 py-2 text-white rounded-lg transition text-sm flex items-center gap-2 disabled:opacity-50 ${deleteImpact?.data?.hasConsumption ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="confirm-delete-btn"
              >
                <Trash2 size={16} />
                {deleteImpact?.data?.hasConsumption ? 'Delete anyway' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit GRN Modal */}
      {showEditModal && editData.id && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Edit2 className="text-amber-500" size={20} />
                <h2 className="font-bold text-lg" data-testid="edit-modal-title">Edit GRN - {editData.id}</h2>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Result & Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Result</label>
                  <select
                    value={editData.result}
                    onChange={(e) => handleEditChange('result', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none font-medium ${
                      editData.result === 'Accepted' ? 'border-green-400 bg-green-50 text-green-700' : 'border-red-400 bg-red-50 text-red-700'
                    }`}
                    data-testid="edit-result-select"
                  >
                    <option value="Accepted">Accepted</option>
                    <option value="Shortage">Shortage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none"
                    data-testid="edit-status-select"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Pending QA">Pending QA</option>
                    <option value="Verified">Verified</option>
                    <option value="Processing">Processing</option>
                  </select>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <input type="text" value={editData.remarks} onChange={(e) => handleEditChange('remarks', e.target.value)}
                  placeholder="Any notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none" data-testid="edit-remarks" />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveGRNEdit}
                disabled={savingGRNEdit}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm font-medium disabled:opacity-50"
                data-testid="save-edit-btn"
              >
                {savingGRNEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {savingGRNEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {imageViewerGRN && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setImageViewerGRN(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Photos / Documents — {imageViewerGRN.id}</h3>
              <button onClick={() => setImageViewerGRN(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4">
              <AttachmentGrid
                attachments={imageViewerGRN.images || imageViewerGRN.documents || []}
                emptyMessage="No attachments available for this GRN. Files uploaded before this update may not have been saved."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GRN;
